import { ChangeEvent, useEffect, useState, useCallback, useRef } from 'react';

import { Grid, Popover, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';

import { isPending } from '../../../../utils/common';
import { StyledTextArea } from '../../../StyledTextArea';
import { GRID_ROW_HEIGHT } from '../../../Table/constants';
import { useAssetsStore } from '../../AssetsProvider';
import { DEFAULT_POPUP_AREA_WIDTH } from '../../helpers/common';

import { TextWrapper } from './styles';

interface TextAreaComponentProps {
  //todo make one type
  id: number | string;
  field: string;
  hasFocus: boolean;
}

export const AssetDescription = observer(
  ({ id, field, hasFocus }: TextAreaComponentProps) => {
    const apiRef = useGridApiContext();
    const ref = useRef(null);
    const { chosenAsset } = useAssetsStore();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const changeCellHandler = useCallback(
      (fieldName: string, cellValue?: string) => {
        return apiRef.current.setEditCellValue({
          id,
          field: fieldName,
          value: cellValue,
        });
      },
      [apiRef, id],
    );

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
      chosenAsset?.setEntity({ description: event.target.value });

    const onBlur = () => changeCellHandler(field, chosenAsset?.description);
    const openPopover = () => !open && setAnchorEl(ref.current);

    useEffect(() => {
      if (hasFocus) {
        setAnchorEl(ref.current);
        if (chosenAsset?.descriptionTruncated)
          chosenAsset?.getFullDescription();
      }
    }, [hasFocus, ref, chosenAsset]);

    return (
      <>
        <Grid overflow="hidden" textOverflow="ellipsis" onClick={openPopover}>
          <Typography
            sx={{
              height: GRID_ROW_HEIGHT,
              width: '100%',
            }}
            ref={ref}
            noWrap>
            {chosenAsset?.description}
          </Typography>
        </Grid>
        {hasFocus && !!chosenAsset && (
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}>
            <TextWrapper>
              {isPending(chosenAsset?.fullDescriptionReq) ? (
                <LinearProgress sx={{ width: DEFAULT_POPUP_AREA_WIDTH }} />
              ) : (
                <StyledTextArea
                  onChange={handleChange}
                  onBlur={onBlur}
                  value={chosenAsset?.description}
                  minRows={5}
                  maxRows={7}
                  sx={{ width: DEFAULT_POPUP_AREA_WIDTH }}
                />
              )}
            </TextWrapper>
          </Popover>
        )}
      </>
    );
  },
);
